import React, { useMemo, useState } from 'react';
import {
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import KitchenOutlinedIcon from '@mui/icons-material/KitchenOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReviewsOutlinedIcon from '@mui/icons-material/ReviewsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLayout } from '../context/LayoutContext';
import { useSettings } from '../context/SettingsContext';
import { useNotification } from '../context/NotificationContext';
import '../assets/styles/Sidebar.scss';

const isPathActive = (pathname, paths, exact = false) => {
  const candidates = Array.isArray(paths) ? paths : [paths];
  return candidates.some((path) => exact
    ? pathname === path
    : pathname === path || pathname.startsWith(`${path}/`));
};

const Sidebar = () => {
  const { currentUser, canAccessModule } = useAuth();
  const { settings, isOperationalModuleAvailable } = useSettings();
  const { sidebarOpen, toggleSidebar } = useLayout();
  const { bubbleCounts } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [menu, setMenu] = useState({ anchor: null, group: null });

  const isCateringBusiness = String(settings?.business_type || '').toUpperCase() === 'CATERING';

  const navGroups = useMemo(() => [
    {
      key: 'home',
      label: 'Home',
      icon: DashboardOutlinedIcon,
      children: [
        { label: 'Dashboard', path: '/dashboard', icon: DashboardOutlinedIcon, module: 'dashboard', exact: true },
        { label: 'Media', path: '/media', icon: PermMediaOutlinedIcon, module: 'dashboard' },
      ],
    },
    {
      key: 'crm',
      label: 'CRM',
      icon: PeopleOutlineIcon,
      children: [
        { label: 'Contacts', path: '/leads', icon: PeopleOutlineIcon, module: 'leads', badge: Number(bubbleCounts?.leadsCount || 0) },
        { label: 'Customers', path: '/customers', icon: GroupsOutlinedIcon, module: 'customers' },
      ],
    },
    {
      key: 'sales',
      label: 'Sales',
      icon: RequestQuoteOutlinedIcon,
      children: [
        { label: 'Quotations', path: '/quotations', paths: ['/quotations', '/quotation-create', '/quotation/create'], icon: RequestQuoteOutlinedIcon, module: 'quotations' },
        { label: 'Orders', path: '/orders', icon: AssignmentOutlinedIcon, module: 'orders' },
      ],
    },
    {
      key: 'billing',
      label: 'Billing',
      icon: ReceiptLongOutlinedIcon,
      children: [
        { label: 'Invoices', path: '/invoices', icon: ReceiptLongOutlinedIcon, module: 'invoices' },
        { label: 'Proforma Invoices', path: '/proforma-invoices', icon: RequestQuoteOutlinedIcon, module: 'invoices' },
        { label: 'Pending Payments', path: '/payments', icon: AccountBalanceWalletOutlinedIcon, module: 'payments' },
        { label: 'Payment History', path: '/payments/history', icon: ReceiptLongOutlinedIcon, module: 'payments' },
        { label: 'Payment Reminders', path: '/payment-reminders', icon: NotificationsActiveOutlinedIcon, module: 'payment_reminders' },
      ],
    },
    {
      key: 'catalog',
      label: 'Catalog',
      icon: Inventory2OutlinedIcon,
      children: [
        { label: 'Products', path: '/products/list', paths: ['/products/list', '/products/new'], icon: Inventory2OutlinedIcon, module: 'products' },
        { label: 'Categories', path: '/categories', icon: CategoryOutlinedIcon, module: 'products' },
        { label: 'Attributes', path: '/attributes', icon: TuneOutlinedIcon, module: 'products' },
        { label: 'Coupons', path: '/coupons', icon: ConfirmationNumberOutlinedIcon, module: 'settings' },
        { label: 'Refer & Earn', path: '/referrals', icon: GroupsOutlinedIcon, module: 'settings' },
      ],
    },
    {
      key: 'operations',
      label: 'Operations',
      icon: KitchenOutlinedIcon,
      children: [
        ...(isCateringBusiness ? [{ label: 'KOT Board', path: '/kots', icon: KitchenOutlinedIcon, module: 'kots', badge: Number(bubbleCounts?.kotCount || 0) }] : []),
        { label: 'Deliveries', path: '/deliveries', icon: LocalShippingOutlinedIcon, module: 'deliveries', badge: Number(bubbleCounts?.deliveryCount || 0) },
        { label: 'Order Feedbacks', path: '/feedbacks', icon: ReviewsOutlinedIcon, module: 'reports' },
      ],
    },
    {
      key: 'storefront',
      label: 'Storefront',
      icon: StorefrontOutlinedIcon,
      operationalModule: 'website',
      children: [
        { label: 'Storefronts', path: '/storefronts', icon: StorefrontOutlinedIcon, module: 'website' },
        { label: 'Build Your Box', path: '/build-your-box', icon: CategoryOutlinedIcon, module: 'products' },
        { label: 'Manage Storefronts', path: '/settings?section=structure', activePath: '/settings', icon: SettingsOutlinedIcon, module: 'settings', settingsSection: 'structure' },
      ],
    },
    {
      key: 'reports',
      label: 'Reports',
      icon: AssessmentOutlinedIcon,
      module: 'reports',
      path: '/reports',
      exact: true,
    },
    {
      key: 'more',
      label: 'More',
      icon: SettingsOutlinedIcon,
      children: [
        { label: 'Settings', path: '/settings', icon: SettingsOutlinedIcon, module: 'settings' },
        ...(currentUser?.role === 'admin' ? [
          { label: 'API Catalog', path: '/developer/api-catalog', icon: CodeOutlinedIcon, module: 'settings' },
          { label: 'Users', path: '/users', icon: ManageAccountsOutlinedIcon, module: 'users' },
        ] : []),
      ],
    },
  ], [bubbleCounts, currentUser?.role, isCateringBusiness]);

  const visibleGroups = useMemo(() => navGroups.map((group) => {
    if (group.operationalModule && !isOperationalModuleAvailable(group.operationalModule)) return null;
    if (!group.children) return canAccessModule(group.module) ? group : null;

    const children = group.children.filter((item) => {
      if (item.module === 'website' && !isOperationalModuleAvailable('website')) return false;
      return canAccessModule(item.module);
    });
    return children.length ? { ...group, children } : null;
  }).filter(Boolean), [navGroups, canAccessModule, isOperationalModuleAvailable]);

  const itemIsActive = (item) => {
    if (item.settingsSection) {
      return location.pathname === '/settings' && new URLSearchParams(location.search).get('section') === item.settingsSection;
    }
    return isPathActive(location.pathname, item.paths || item.activePath || item.path, item.exact);
  };

  const groupIsActive = (group) => group.path
    ? isPathActive(location.pathname, group.path, group.exact)
    : group.children?.some(itemIsActive);

  const closeMenu = () => setMenu({ anchor: null, group: null });
  const openGroup = (event, group) => {
    if (!group.children) {
      navigate(group.path);
      closeMenu();
      return;
    }
    setMenu({ anchor: event.currentTarget, group });
  };
  const chooseItem = (path) => {
    closeMenu();
    navigate(path);
  };

  const badge = (count) => {
    const safeCount = Number(count || 0);
    if (!safeCount) return null;
    return <span className="hub-sidebar__badge">{safeCount > 99 ? '99+' : safeCount}</span>;
  };

  return (
    <aside className={`hub-sidebar ${sidebarOpen ? '' : 'hub-sidebar--collapsed'}`} aria-label="Main navigation">
      <List className="hub-sidebar__nav" disablePadding>
        {visibleGroups.map((group) => {
          const Icon = group.icon;
          const groupBadge = (group.children || []).reduce((sum, item) => sum + Number(item.badge || 0), 0);
          return (
            <Tooltip key={group.key} title={sidebarOpen ? '' : group.label} placement="right">
              <ListItemButton
                className={`hub-sidebar__item ${groupIsActive(group) ? 'is-active' : ''}`}
                onClick={(event) => openGroup(event, group)}
                aria-haspopup={Boolean(group.children)}
                aria-expanded={menu.group?.key === group.key}
              >
                <ListItemIcon><Icon /></ListItemIcon>
                {sidebarOpen ? <ListItemText primary={group.label} /> : null}
                {sidebarOpen ? badge(groupBadge) : null}
                {sidebarOpen && group.children ? <ChevronRightIcon className="hub-sidebar__chevron" /> : null}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>

      <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'} placement="right">
        <button className="hub-sidebar__collapse" type="button" onClick={toggleSidebar} aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          {sidebarOpen ? <span>Collapse sidebar</span> : null}
        </button>
      </Tooltip>

      <div className="hub-sidebar__account">
        <Avatar>{String(currentUser?.name || currentUser?.email || 'U').charAt(0).toUpperCase()}</Avatar>
        {sidebarOpen ? <span>{currentUser?.name || currentUser?.email || 'My account'}</span> : null}
      </div>

      <Popover
        open={Boolean(menu.anchor)}
        anchorEl={menu.anchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: 'hub-sidebar__flyout' } }}
      >
        <Typography className="hub-sidebar__flyout-title">{menu.group?.label}</Typography>
        <List disablePadding>
          {menu.group?.children?.map((item) => {
            const Icon = item.icon;
            return (
              <ListItemButton key={`${item.label}-${item.path}`} onClick={() => chooseItem(item.path)} className={itemIsActive(item) ? 'is-active' : ''}>
                <ListItemIcon><Icon /></ListItemIcon>
                <ListItemText primary={item.label} />
                {badge(item.badge)}
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </aside>
  );
};

export default Sidebar;
